import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DollarSign, TrendingUp, Coins, Download } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface EarningsTrackerProps {
  totalCoins: number;
  monthlyCoins: number;
  conversionRate?: number; // coins to USD
}

export const EarningsTracker = ({ totalCoins, monthlyCoins, conversionRate = 0.01 }: EarningsTrackerProps) => {
  const { user } = useAuth();
  const totalUSD = (totalCoins * conversionRate).toFixed(2);
  const monthlyUSD = (monthlyCoins * conversionRate).toFixed(2);
  const monthlyGoal = 10000; // coins
  const monthlyProgress = (monthlyCoins / monthlyGoal) * 100;

  const exportEarnings = async () => {
    if (!user) return;

    try {
      // Fetch transactions for tax export
      const { data: transactions } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .in('type', ['tip', 'purchase', 'payout'])
        .order('created_at', { ascending: false });

      const csv = [
        ['Date', 'Type', 'Amount (Coins)', 'Amount (USD)', 'Balance After', 'Reference'].join(','),
        ...(transactions || []).map(t => [
          new Date(t.created_at).toLocaleDateString(),
          t.type,
          t.amount,
          (t.amount * conversionRate).toFixed(2),
          t.balance_after,
          t.reference || 'N/A'
        ].join(','))
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earnings-${new Date().getFullYear()}.csv`;
      a.click();
      toast.success('Earnings exported for tax purposes');
    } catch (error) {
      toast.error('Failed to export earnings');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={exportEarnings}>
          <Download className="h-4 w-4 mr-2" />
          Export for Tax
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Total Earnings</CardTitle>
          <Coins className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalCoins.toLocaleString()} coins</div>
          <p className="text-xs text-muted-foreground mt-1">
            ≈ ${totalUSD} USD
          </p>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">This Month</CardTitle>
          <TrendingUp className="h-4 w-4 text-secondary" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{monthlyCoins.toLocaleString()} coins</div>
          <p className="text-xs text-muted-foreground mt-1">
            ≈ ${monthlyUSD} USD
          </p>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium">Monthly Goal</CardTitle>
          <DollarSign className="h-4 w-4 text-accent" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{monthlyCoins.toLocaleString()}</span>
              <span className="text-muted-foreground">{monthlyGoal.toLocaleString()}</span>
            </div>
            <Progress value={monthlyProgress} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {Math.round(monthlyProgress)}% complete
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};